// src/components/Branch/config/sections/HighlightsSection.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BadgeCheckIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
} from "@heroicons/react/outline";
import {
  updateHighlightsThunk,
  addHighlightThunk,
  deleteHighlightThunk,
} from "../../../../store/thunks/branchDisplayThunks";
import Dropdown from "../../../common/Dropdown";

const HIGHLIGHT_TYPES = [
  { _id: "YEARS_IN_BUSINESS", name: "Years in Business" },
  { _id: "HAPPY_CUSTOMERS", name: "Happy Customers" },
  { _id: "PRODUCTS_SOLD", name: "Products Sold" },
  { _id: "DESIGNS", name: "Designs" },
  { _id: "CRAFTSMEN", name: "Craftsmen" },
  { _id: "AWARDS", name: "Awards" },
  { _id: "CERTIFICATIONS", name: "Certifications" },
  { _id: "BRANCHES", name: "Branches" },
  { _id: "CUSTOM", name: "Custom" },
];

const HighlightsSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { highlights, loadingSection } = useSelector((state) => state.branchDisplay);

  const [isAdding, setIsAdding] = useState(false);
  const [newHighlight, setNewHighlight] = useState({
    type: "CUSTOM",
    label: "",
    value: "",
    icon: "",
    isVisible: true,
  });

  const handleAdd = async () => {
    await dispatch(addHighlightThunk({ branchCode, highlight: newHighlight }));
    setIsAdding(false);
    setNewHighlight({ type: "CUSTOM", label: "", value: "", icon: "", isVisible: true });
  };

  const handleDelete = async (index) => {
    if (window.confirm("Delete this highlight?")) {
      await dispatch(deleteHighlightThunk({ branchCode, highlightIndex: index }));
    }
  };

  return (
    <div className="space-y-6">
      {isRootAdmin && !isAdding && (
        <button
          onClick={() => setIsAdding(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Add Highlight
        </button>
      )}

      {isAdding && (
        <div className="bg-white border rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Dropdown
              label="Highlight Type"
              options={HIGHLIGHT_TYPES}
              selected={HIGHLIGHT_TYPES.find((t) => t._id === newHighlight.type)}
              onChange={(selected) =>
                setNewHighlight((prev) => ({ ...prev, type: selected?._id || "CUSTOM" }))
              }
            />
            <div>
              <label className="block text-sm text-gray-600 mb-1">Label *</label>
              <input
                type="text"
                value={newHighlight.label}
                onChange={(e) =>
                  setNewHighlight((prev) => ({ ...prev, label: e.target.value }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="e.g., Happy Customers"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Value *</label>
              <input
                type="text"
                value={newHighlight.value}
                onChange={(e) =>
                  setNewHighlight((prev) => ({ ...prev, value: e.target.value }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="e.g., 50000+"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Icon (optional)</label>
              <input
                type="text"
                value={newHighlight.icon}
                onChange={(e) =>
                  setNewHighlight((prev) => ({ ...prev, icon: e.target.value }))
                }
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Icon name or emoji"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={!newHighlight.label || !newHighlight.value}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
            >
              <CheckCircleIcon className="h-4 w-4" />
              Add
            </button>
            <button
              onClick={() => setIsAdding(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center">
          <BadgeCheckIcon className="h-4 w-4 mr-2" />
          Store Highlights ({highlights.length})
        </h4>

        {highlights.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No highlights added</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {highlights.map((highlight, index) => (
              <div
                key={index}
                className={`bg-white border rounded-lg p-4 text-center relative ${
                  !highlight.isVisible ? "opacity-60" : ""
                }`}
              >
                {isRootAdmin && (
                  <button
                    onClick={() => handleDelete(index)}
                    className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
                <p className="text-2xl font-bold text-blue-600">{highlight.value}</p>
                <p className="text-sm text-gray-600">{highlight.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HighlightsSection;
